import {
  resolveIsAgentixRxDefault,
  resolveIsAgentixRxEnabled,
} from "./agentixRxAccess";

export const AGENTIC_RX_PATH = "/ai-prescription";
export const NEW_RX_PATH = "/newrxpad";

export type RxExperiencePath = typeof AGENTIC_RX_PATH | typeof NEW_RX_PATH;

export const RX_EXPERIENCE_PROMPT_STATE_KEY = "rxExperiencePrompt";

export type RxExperiencePrompt = "choose" | "confirm";

const STORAGE_KEY = "ai.rxExperiencePref";

type PreferenceStore = {
  defaultPath: RxExperiencePath | null;
  used: {
    [AGENTIC_RX_PATH]: boolean;
    [NEW_RX_PATH]: boolean;
  };
};

const EMPTY_STORE: PreferenceStore = {
  defaultPath: null,
  used: {
    [AGENTIC_RX_PATH]: false,
    [NEW_RX_PATH]: false,
  },
};

function isRxExperiencePath(value: unknown): value is RxExperiencePath {
  return value === AGENTIC_RX_PATH || value === NEW_RX_PATH;
}

function readStore(): PreferenceStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY_STORE, used: { ...EMPTY_STORE.used } };
    const parsed = JSON.parse(raw);
    return {
      defaultPath: isRxExperiencePath(parsed?.defaultPath) ? parsed.defaultPath : null,
      used: {
        [AGENTIC_RX_PATH]: parsed?.used?.[AGENTIC_RX_PATH] === true,
        [NEW_RX_PATH]: parsed?.used?.[NEW_RX_PATH] === true,
      },
    };
  } catch {
    return { ...EMPTY_STORE, used: { ...EMPTY_STORE.used } };
  }
}

function writeStore(store: PreferenceStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* localStorage unavailable */
  }
}

export function rxExperienceFromPathname(pathname: string): RxExperiencePath | null {
  if (pathname.startsWith(AGENTIC_RX_PATH)) return AGENTIC_RX_PATH;
  if (pathname.startsWith(NEW_RX_PATH)) return NEW_RX_PATH;
  return null;
}

/** Main pad screens only — not New Rx summary or other nested pad routes. */
export function isRxExperienceHome(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return normalized === AGENTIC_RX_PATH || normalized === NEW_RX_PATH;
}

export function getRxExperienceLabel(path: RxExperiencePath): string {
  return path === AGENTIC_RX_PATH ? "AgenticRx" : "New Rx Pad";
}

function flagDefaultPath(servicesData?: any): RxExperiencePath {
  return resolveIsAgentixRxDefault(servicesData) ? AGENTIC_RX_PATH : NEW_RX_PATH;
}

/**
 * Resolved default pad:
 * 1. New Rx Pad if AgenticRx is not enabled
 * 2. User's saved choice, if they picked one
 * 3. Account flag `isAgentixRxDefault` (TEMP fallback until the API field exists)
 */
export function getDefaultRxExperience(servicesData?: any): RxExperiencePath {
  if (!resolveIsAgentixRxEnabled(servicesData)) return NEW_RX_PATH;
  const stored = readStore().defaultPath;
  if (stored === AGENTIC_RX_PATH) return AGENTIC_RX_PATH;
  if (stored === NEW_RX_PATH) return NEW_RX_PATH;
  return flagDefaultPath(servicesData);
}

export function setDefaultRxExperience(path: RxExperiencePath): void {
  const store = readStore();
  store.defaultPath = path;
  writeStore(store);
}

export function markRxExperienceUsed(path: RxExperiencePath): void {
  const store = readStore();
  if (store.used[path]) return;
  store.used[path] = true;
  writeStore(store);
}

export function hasUsedBothRxExperiences(): boolean {
  const { used } = readStore();
  return used[AGENTIC_RX_PATH] && used[NEW_RX_PATH];
}

/** Generic Rx entry (login, Patient Search, Go to RxPad). */
export function getRxExperienceEntryPath(servicesData?: any): RxExperiencePath {
  return getDefaultRxExperience(servicesData);
}

export function promptForRxSwitch(
  from: RxExperiencePath,
  to: RxExperiencePath,
  servicesData?: any
): RxExperiencePrompt | undefined {
  if (from === to) return undefined;
  if (to === AGENTIC_RX_PATH && !resolveIsAgentixRxEnabled(servicesData)) {
    return undefined;
  }
  if (getDefaultRxExperience(servicesData) !== to) return "confirm";
  return undefined;
}
